/**
 * VENUE_PACK wizard — admin creates a Venues bundle by picking already-
 * ingested stadiums from the SYSTEM catalog. Backend copies each pick
 * into a PackVenue template row; on purchase, the existing
 * _clone_pack_contents path clones into the buyer's tenant as
 * USER-owned Stadium rows.
 *
 * Opens when /admin/marketplace mounts with ?action=new&type=VENUE_PACK.
 *
 * 2026-07-18a — CSV upload → catalog picker.
 * 2026-07-18b — full-screen layout via WizardShell (sidebar + main).
 */
import { useState } from "react";
import { Sparkles, CheckCircle2, MapPin, Save } from "lucide-react";
import {
  createPack,
  updatePack,
  publishPack,
  listCatalogVenues,
  selectVenuesFromCatalog,
} from "../../api/owner.api";
import CatalogPicker from "../../components/marketplace/CatalogPicker";
import PackDetailsForm from "../../components/marketplace/PackDetailsForm";
import WizardShell from "../../components/marketplace/WizardShell";

export default function VenuePackWizard({ onClose, onCreated }) {
  const [details, setDetails] = useState({
    name: "",
    season: "",
    short_description: "",
    description: "",
    price_credits: 49,
  });
  const [venueIds, setVenueIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [testMode, setTestMode] = useState(true);
  // Draft persistence — see SquadPackWizard for rationale.
  const [packId, setPackId] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  function detailsValid() {
    return details.name.trim().length >= 3
      && Number.isFinite(Number(details.price_credits))
      && Number(details.price_credits) >= 0;
  }

  function _packBody() {
    return {
      name: details.name.trim(),
      season: details.season.trim(),
      short_description: details.short_description.trim(),
      description: details.description.trim(),
      price_credits: Number(details.price_credits),
    };
  }

  async function _persist() {
    let pack;
    if (packId) {
      const { data } = await updatePack(packId, _packBody());
      pack = data;
    } else {
      const slug = details.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 80);
      const { data } = await createPack({
        ..._packBody(),
        slug,
        pack_type: "VENUE_PACK",
      });
      pack = data;
      setPackId(pack.id);
    }
    const { data: summary } = await selectVenuesFromCatalog(pack.id, venueIds);
    return { pack, summary };
  }

  function _errorMessage(e) {
    return e?.response?.data
      ? (typeof e.response.data === "string"
          ? e.response.data
          : (e.response.data.detail
              || e.response.data.error
              || JSON.stringify(e.response.data)))
      : e.message;
  }

  function _validate() {
    if (!detailsValid()) {
      setError("Fill in name (≥ 3 chars) and a non-negative price first.");
      return false;
    }
    if (venueIds.length === 0) {
      setError("Pick at least one stadium from the catalog.");
      return false;
    }
    return true;
  }

  async function handleSaveDraft() {
    if (!_validate()) return;
    setBusy(true);
    setError(null);
    try {
      const { pack } = await _persist();
      setSavedAt(new Date());
      if (onCreated) onCreated(pack);
    } catch (e) {
      setError(_errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleCreate() {
    if (!_validate()) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const { pack, summary } = await _persist();
      const { data: published } = await publishPack(pack.id, {
        skipPromotion: testMode,
      });

      setResult({
        pack_name: published.name,
        pack_id: pack.id,
        venues_created: summary.venues_created || 0,
        venues: summary.venues || [],
        errors: summary.errors || [],
        test_mode: testMode,
      });
      if (onCreated) onCreated(pack);
    } catch (e) {
      setError(_errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const sidebar = result ? (
    <SummarySidebar
      title="Venue pack created"
      lines={[
        `${result.venues_created} venue${result.venues_created === 1 ? "" : "s"} added`,
        result.test_mode ? "Published in test mode." : "Promotion blast fired.",
      ]}
    />
  ) : (
    <>
      <PackDetailsForm
        details={details}
        setDetails={setDetails}
        namePlaceholder="India — International venues 2026"
        shortDescriptionPlaceholder="8 international venues in India"
        testMode={testMode}
        setTestMode={setTestMode}
      />
      <div className="pt-3 border-t border-border-default text-xs text-text-secondary">
        <div className="flex items-center gap-1.5 text-text-primary font-medium mb-1">
          <MapPin className="w-3.5 h-3.5" /> {venueIds.length} venue{venueIds.length === 1 ? "" : "s"} picked
        </div>
        Pick from the SYSTEM stadium catalog on the right.
      </div>

      {savedAt && (
        <div className="pt-3 border-t border-border-default text-xs text-emerald-300 flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Draft saved · {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
      )}
    </>
  );

  const isSaved = Boolean(packId);
  return (
    <WizardShell
      eyebrow="Marketplace · VENUE_PACK"
      title={result ? "Venue pack created" : "New venues bundle"}
      onClose={onClose}
      busy={busy}
      error={error}
      sidebar={sidebar}
      primaryLabel={result ? "Done" : (isSaved ? "Publish" : "Create & publish")}
      primaryIcon={result ? null : <Sparkles className="w-4 h-4" />}
      primaryDisabled={!result && (!detailsValid() || venueIds.length === 0)}
      primaryTone="teal"
      onPrimary={result ? onClose : handleCreate}
      secondaryLabel={result ? undefined : (isSaved ? "Update draft" : "Save draft")}
      secondaryIcon={result ? null : <Save className="w-4 h-4" />}
      secondaryDisabled={!detailsValid() || venueIds.length === 0}
      onSecondary={handleSaveDraft}
    >
      {!result ? (
        <section className="space-y-3">
          <div>
            <h3 className="text-base font-semibold">Pick venues from the catalog</h3>
            <p className="text-sm text-text-secondary mt-0.5">
              Only SYSTEM-owned stadiums the platform has already ingested are
              shown. Search by name, city, or state.
            </p>
          </div>
          <CatalogPicker
            fetchFn={(params) => listCatalogVenues(params)}
            selectedIds={venueIds}
            onChange={setVenueIds}
            placeholder="Search stadiums by name, city, state…"
            emptyHint="No stadiums match. Try a different search."
            renderRow={(s) => (
              <div className="flex items-baseline justify-between gap-2">
                <div className="min-w-0">
                  <span className="font-medium">{s.name}</span>
                  {(s.city || s.state) && (
                    <span className="ml-2 text-xs text-text-secondary">
                      {[s.city, s.state].filter(Boolean).join(", ")}
                    </span>
                  )}
                </div>
                {s.nationality_name && (
                  <span className="text-[10px] uppercase tracking-wider text-text-secondary shrink-0">
                    {s.nationality_name}
                  </span>
                )}
              </div>
            )}
            renderChip={(s) => (
              <span>{s.name}{s.city ? ` · ${s.city}` : ""}</span>
            )}
          />
        </section>
      ) : (
        <ResultView
          headerIcon={<CheckCircle2 className="w-5 h-5" />}
          headerText={`Created “${result.pack_name}”`}
          tableTitle="Venues in this pack"
          rows={result.venues.map((v) => ({
            key: v.venue_id,
            left: v.name,
            right: [v.city, v.country].filter(Boolean).join(", "),
          }))}
          errors={result.errors.map((e) => ({
            id: e.venue_id, reason: e.reason,
          }))}
        />
      )}
    </WizardShell>
  );
}

function SummarySidebar({ title, lines }) {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold uppercase tracking-widest text-text-secondary">
        {title}
      </h3>
      <ul className="text-sm text-text-primary space-y-1">
        {lines.map((l, i) => <li key={i}>{l}</li>)}
      </ul>
    </div>
  );
}

function ResultView({ headerIcon, headerText, tableTitle, rows, errors }) {
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-emerald-200">
        <div className="flex items-center gap-2 font-semibold">
          {headerIcon} {headerText}
        </div>
      </div>
      {rows.length > 0 && (
        <div className="rounded-md border border-border-default bg-bg-secondary/40">
          <div className="px-3 py-2 text-xs uppercase tracking-wider text-text-secondary border-b border-border-default">
            {tableTitle}
          </div>
          <table className="w-full text-sm">
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t border-border-default first:border-t-0">
                  <td className="px-3 py-1.5">{r.left}</td>
                  <td className="px-3 py-1.5 text-text-secondary">{r.right}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {errors.length > 0 && (
        <details className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-200 text-sm">
          <summary className="cursor-pointer font-medium">
            {errors.length} row{errors.length === 1 ? "" : "s"} skipped
          </summary>
          <ul className="mt-2 ml-3 space-y-1 text-xs">
            {errors.slice(0, 20).map((e, i) => (
              <li key={i}>
                {e.id ? `${e.id}: ` : ""}{e.reason}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
